/**
 * Sales Tax Calculator for Engraveca
 * Business Nexus: Santa Ana, CA 92705
 * 
 * California Sales Tax Structure:
 * - State Tax: 6.00%
 * - County Tax: 0.25%
 * - City Tax: varies by city
 * - District Tax: varies by district
 * 
 * Total rates range from 7.25% to ~10.75% depending on location
 */

// California county tax rates (base + county)
const CA_COUNTY_RATES = {
  // Orange County cities
  'ORANGE': {
    baseRate: 7.75,
    cities: {
      'SANTA ANA': 8.75,
      'ANAHEIM': 7.75,
      'IRVINE': 7.75,
      'COSTA MESA': 7.75,
      'NEWPORT BEACH': 7.75,
      'HUNTINGTON BEACH': 7.75,
      'GARDEN GROVE': 8.75,
      'ORANGE': 8.75,
      'FULLERTON': 7.75,
      'TUSTIN': 7.75,
      'LAGUNA BEACH': 7.75,
      'MISSION VIEJO': 7.75,
      'LAKE FOREST': 7.75,
      'BREA': 8.75,
    }
  },
  // Los Angeles County
  'LOS ANGELES': {
    baseRate: 9.50,
    cities: {
      'LOS ANGELES': 9.50,
      'LONG BEACH': 10.25,
      'SANTA MONICA': 10.25,
      'PASADENA': 10.25,
      'TORRANCE': 10.00,
      'BURBANK': 10.25,
      'GLENDALE': 10.25,
      'POMONA': 10.25,
      'WEST HOLLYWOOD': 10.25,
      'BEVERLY HILLS': 10.25,
    }
  },
  // San Diego County
  'SAN DIEGO': {
    baseRate: 7.75,
    cities: {
      'SAN DIEGO': 7.75,
      'CHULA VISTA': 8.75,
      'OCEANSIDE': 7.75,
      'ESCONDIDO': 7.75,
      'CARLSBAD': 7.75,
      'VISTA': 7.75,
      'LA MESA': 8.75,
      'NATIONAL CITY': 8.75,
    }
  },
  // San Francisco County
  'SAN FRANCISCO': {
    baseRate: 8.625,
    cities: {
      'SAN FRANCISCO': 8.625,
    }
  },
  // Alameda County
  'ALAMEDA': {
    baseRate: 10.25,
    cities: {
      'OAKLAND': 10.25,
      'BERKELEY': 10.25,
      'FREMONT': 10.25,
      'HAYWARD': 10.75,
      'ALAMEDA': 10.75,
    }
  },
  // Santa Clara County
  'SANTA CLARA': {
    baseRate: 9.125,
    cities: {
      'SAN JOSE': 9.375,
      'SANTA CLARA': 9.125,
      'SUNNYVALE': 9.125,
      'MOUNTAIN VIEW': 9.125,
      'PALO ALTO': 9.125,
      'CUPERTINO': 9.125,
      'MILPITAS': 9.25,
    }
  },
  // Riverside County
  'RIVERSIDE': {
    baseRate: 7.75,
    cities: {
      'RIVERSIDE': 8.75,
      'CORONA': 8.75,
      'MORENO VALLEY': 8.75,
      'TEMECULA': 7.75,
      'MURRIETA': 7.75,
      'PALM SPRINGS': 9.25,
    }
  },
  // San Bernardino County
  'SAN BERNARDINO': {
    baseRate: 7.75,
    cities: {
      'SAN BERNARDINO': 8.00,
      'FONTANA': 7.75,
      'ONTARIO': 7.75,
      'RANCHO CUCAMONGA': 7.75,
      'VICTORVILLE': 7.75,
      'REDLANDS': 8.00,
    }
  },
  // Sacramento County
  'SACRAMENTO': {
    baseRate: 8.75,
    cities: {
      'SACRAMENTO': 8.75,
      'ELK GROVE': 8.75,
      'ROSEVILLE': 7.25,
      'FOLSOM': 7.75,
    }
  },
};

// ZIP code to county/city mapping for California
// This is a simplified mapping - in production, you'd use a comprehensive database
const CA_ZIP_MAPPING = {
  // Orange County - Santa Ana (our nexus)
  '92701': { county: 'ORANGE', city: 'SANTA ANA' },
  '92702': { county: 'ORANGE', city: 'SANTA ANA' },
  '92703': { county: 'ORANGE', city: 'SANTA ANA' },
  '92704': { county: 'ORANGE', city: 'SANTA ANA' },
  '92705': { county: 'ORANGE', city: 'SANTA ANA' }, // Our business location
  '92706': { county: 'ORANGE', city: 'SANTA ANA' },
  '92707': { county: 'ORANGE', city: 'SANTA ANA' },
  '92708': { county: 'ORANGE', city: 'FOUNTAIN VALLEY' },
  
  // Orange County - Other cities
  '92801': { county: 'ORANGE', city: 'ANAHEIM' },
  '92802': { county: 'ORANGE', city: 'ANAHEIM' },
  '92803': { county: 'ORANGE', city: 'ANAHEIM' },
  '92804': { county: 'ORANGE', city: 'ANAHEIM' },
  '92805': { county: 'ORANGE', city: 'ANAHEIM' },
  '92806': { county: 'ORANGE', city: 'ANAHEIM' },
  '92807': { county: 'ORANGE', city: 'ANAHEIM' },
  '92808': { county: 'ORANGE', city: 'ANAHEIM' },
  
  '92602': { county: 'ORANGE', city: 'IRVINE' },
  '92603': { county: 'ORANGE', city: 'IRVINE' },
  '92604': { county: 'ORANGE', city: 'IRVINE' },
  '92606': { county: 'ORANGE', city: 'IRVINE' },
  '92612': { county: 'ORANGE', city: 'IRVINE' },
  '92614': { county: 'ORANGE', city: 'IRVINE' },
  '92617': { county: 'ORANGE', city: 'IRVINE' },
  '92618': { county: 'ORANGE', city: 'IRVINE' },
  '92620': { county: 'ORANGE', city: 'IRVINE' },
  
  '92626': { county: 'ORANGE', city: 'COSTA MESA' },
  '92627': { county: 'ORANGE', city: 'COSTA MESA' },
  '92628': { county: 'ORANGE', city: 'COSTA MESA' },
  
  '92660': { county: 'ORANGE', city: 'NEWPORT BEACH' },
  '92661': { county: 'ORANGE', city: 'NEWPORT BEACH' },
  '92662': { county: 'ORANGE', city: 'NEWPORT BEACH' },
  '92663': { county: 'ORANGE', city: 'NEWPORT BEACH' },
  
  '92646': { county: 'ORANGE', city: 'HUNTINGTON BEACH' },
  '92647': { county: 'ORANGE', city: 'HUNTINGTON BEACH' },
  '92648': { county: 'ORANGE', city: 'HUNTINGTON BEACH' },
  '92649': { county: 'ORANGE', city: 'HUNTINGTON BEACH' },
  
  '92840': { county: 'ORANGE', city: 'GARDEN GROVE' },
  '92841': { county: 'ORANGE', city: 'GARDEN GROVE' },
  '92843': { county: 'ORANGE', city: 'GARDEN GROVE' },
  '92844': { county: 'ORANGE', city: 'GARDEN GROVE' },
  '92845': { county: 'ORANGE', city: 'GARDEN GROVE' },
  
  '92856': { county: 'ORANGE', city: 'ORANGE' },
  '92857': { county: 'ORANGE', city: 'ORANGE' },
  '92862': { county: 'ORANGE', city: 'ORANGE' },
  '92863': { county: 'ORANGE', city: 'ORANGE' },
  '92864': { county: 'ORANGE', city: 'ORANGE' },
  '92865': { county: 'ORANGE', city: 'ORANGE' },
  '92866': { county: 'ORANGE', city: 'ORANGE' },
  '92867': { county: 'ORANGE', city: 'ORANGE' },
  '92868': { county: 'ORANGE', city: 'ORANGE' },
  '92869': { county: 'ORANGE', city: 'ORANGE' },
  
  '92831': { county: 'ORANGE', city: 'FULLERTON' },
  '92832': { county: 'ORANGE', city: 'FULLERTON' },
  '92833': { county: 'ORANGE', city: 'FULLERTON' },
  '92834': { county: 'ORANGE', city: 'FULLERTON' },
  '92835': { county: 'ORANGE', city: 'FULLERTON' },
  '92836': { county: 'ORANGE', city: 'FULLERTON' },
  '92837': { county: 'ORANGE', city: 'FULLERTON' },
  '92838': { county: 'ORANGE', city: 'FULLERTON' },
  
  '92780': { county: 'ORANGE', city: 'TUSTIN' },
  '92781': { county: 'ORANGE', city: 'TUSTIN' },
  '92782': { county: 'ORANGE', city: 'TUSTIN' },
  
  '92651': { county: 'ORANGE', city: 'LAGUNA BEACH' },
  '92652': { county: 'ORANGE', city: 'LAGUNA BEACH' },
  
  '92691': { county: 'ORANGE', city: 'MISSION VIEJO' },
  '92692': { county: 'ORANGE', city: 'MISSION VIEJO' },
  
  '92630': { county: 'ORANGE', city: 'LAKE FOREST' },
  
  '92821': { county: 'ORANGE', city: 'BREA' },
  '92822': { county: 'ORANGE', city: 'BREA' },
  '92823': { county: 'ORANGE', city: 'BREA' },
  
  // Los Angeles County
  '90001': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90002': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90003': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90004': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90005': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90006': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90007': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90008': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90010': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90012': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90013': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90014': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90015': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90017': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90019': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90020': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90024': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90025': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90027': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90028': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90029': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90036': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90046': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90048': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90049': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90064': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90067': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90077': { county: 'LOS ANGELES', city: 'LOS ANGELES' },
  '90210': { county: 'LOS ANGELES', city: 'BEVERLY HILLS' },
  '90211': { county: 'LOS ANGELES', city: 'BEVERLY HILLS' },
  '90212': { county: 'LOS ANGELES', city: 'BEVERLY HILLS' },
  '90069': { county: 'LOS ANGELES', city: 'WEST HOLLYWOOD' },
  '90401': { county: 'LOS ANGELES', city: 'SANTA MONICA' },
  '90402': { county: 'LOS ANGELES', city: 'SANTA MONICA' },
  '90403': { county: 'LOS ANGELES', city: 'SANTA MONICA' },
  '90404': { county: 'LOS ANGELES', city: 'SANTA MONICA' },
  '90405': { county: 'LOS ANGELES', city: 'SANTA MONICA' },
  '90802': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '90803': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '90804': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '90805': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '90806': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '90807': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '90808': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '90810': { county: 'LOS ANGELES', city: 'LONG BEACH' },
  '91101': { county: 'LOS ANGELES', city: 'PASADENA' },
  '91103': { county: 'LOS ANGELES', city: 'PASADENA' },
  '91104': { county: 'LOS ANGELES', city: 'PASADENA' },
  '91105': { county: 'LOS ANGELES', city: 'PASADENA' },
  '91106': { county: 'LOS ANGELES', city: 'PASADENA' },
  '91501': { county: 'LOS ANGELES', city: 'BURBANK' },
  '91502': { county: 'LOS ANGELES', city: 'BURBANK' },
  '91504': { county: 'LOS ANGELES', city: 'BURBANK' },
  '91505': { county: 'LOS ANGELES', city: 'BURBANK' },
  '91506': { county: 'LOS ANGELES', city: 'BURBANK' },
  '91201': { county: 'LOS ANGELES', city: 'GLENDALE' },
  '91202': { county: 'LOS ANGELES', city: 'GLENDALE' },
  '91203': { county: 'LOS ANGELES', city: 'GLENDALE' },
  '91204': { county: 'LOS ANGELES', city: 'GLENDALE' },
  '91205': { county: 'LOS ANGELES', city: 'GLENDALE' },
  '91206': { county: 'LOS ANGELES', city: 'GLENDALE' },
  '91207': { county: 'LOS ANGELES', city: 'GLENDALE' },
  '90501': { county: 'LOS ANGELES', city: 'TORRANCE' },
  '90502': { county: 'LOS ANGELES', city: 'TORRANCE' },
  '90503': { county: 'LOS ANGELES', city: 'TORRANCE' },
  '90504': { county: 'LOS ANGELES', city: 'TORRANCE' },
  '90505': { county: 'LOS ANGELES', city: 'TORRANCE' },
  
  // San Diego County
  '92101': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92102': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92103': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92104': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92105': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92106': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92107': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92108': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92109': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92110': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92111': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92113': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92114': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92115': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92116': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92117': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92118': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92119': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92120': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92121': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92122': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92123': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92124': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92126': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92127': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92128': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92129': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '92130': { county: 'SAN DIEGO', city: 'SAN DIEGO' },
  '91910': { county: 'SAN DIEGO', city: 'CHULA VISTA' },
  '91911': { county: 'SAN DIEGO', city: 'CHULA VISTA' },
  '91913': { county: 'SAN DIEGO', city: 'CHULA VISTA' },
  '91914': { county: 'SAN DIEGO', city: 'CHULA VISTA' },
  '91915': { county: 'SAN DIEGO', city: 'CHULA VISTA' },
  '92054': { county: 'SAN DIEGO', city: 'OCEANSIDE' },
  '92056': { county: 'SAN DIEGO', city: 'OCEANSIDE' },
  '92057': { county: 'SAN DIEGO', city: 'OCEANSIDE' },
  '92008': { county: 'SAN DIEGO', city: 'CARLSBAD' },
  '92009': { county: 'SAN DIEGO', city: 'CARLSBAD' },
  '92010': { county: 'SAN DIEGO', city: 'CARLSBAD' },
  '92011': { county: 'SAN DIEGO', city: 'CARLSBAD' },
  
  // San Francisco
  '94102': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94103': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94104': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94105': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94107': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94108': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94109': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94110': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94111': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94112': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94114': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94115': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94116': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94117': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94118': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94121': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94122': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94123': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94124': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94127': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94131': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94132': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94133': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  '94134': { county: 'SAN FRANCISCO', city: 'SAN FRANCISCO' },
  
  // Bay Area - Alameda County
  '94601': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94602': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94603': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94605': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94606': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94607': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94608': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94609': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94610': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94611': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94612': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94619': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94621': { county: 'ALAMEDA', city: 'OAKLAND' },
  '94702': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94703': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94704': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94705': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94706': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94707': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94708': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94709': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94710': { county: 'ALAMEDA', city: 'BERKELEY' },
  '94536': { county: 'ALAMEDA', city: 'FREMONT' },
  '94538': { county: 'ALAMEDA', city: 'FREMONT' },
  '94539': { county: 'ALAMEDA', city: 'FREMONT' },
  '94555': { county: 'ALAMEDA', city: 'FREMONT' },
  
  // Santa Clara County (Silicon Valley)
  '95110': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95111': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95112': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95113': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95116': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95117': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95118': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95119': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95120': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95121': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95122': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95123': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95124': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95125': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95126': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95127': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95128': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95129': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95130': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95131': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95132': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95133': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95134': { county: 'SANTA CLARA', city: 'SAN JOSE' },
  '95035': { county: 'SANTA CLARA', city: 'MILPITAS' },
  '95050': { county: 'SANTA CLARA', city: 'SANTA CLARA' },
  '95051': { county: 'SANTA CLARA', city: 'SANTA CLARA' },
  '95054': { county: 'SANTA CLARA', city: 'SANTA CLARA' },
  '94085': { county: 'SANTA CLARA', city: 'SUNNYVALE' },
  '94086': { county: 'SANTA CLARA', city: 'SUNNYVALE' },
  '94087': { county: 'SANTA CLARA', city: 'SUNNYVALE' },
  '94089': { county: 'SANTA CLARA', city: 'SUNNYVALE' },
  '94040': { county: 'SANTA CLARA', city: 'MOUNTAIN VIEW' },
  '94041': { county: 'SANTA CLARA', city: 'MOUNTAIN VIEW' },
  '94043': { county: 'SANTA CLARA', city: 'MOUNTAIN VIEW' },
  '94301': { county: 'SANTA CLARA', city: 'PALO ALTO' },
  '94303': { county: 'SANTA CLARA', city: 'PALO ALTO' },
  '94304': { county: 'SANTA CLARA', city: 'PALO ALTO' },
  '94306': { county: 'SANTA CLARA', city: 'PALO ALTO' },
  '95014': { county: 'SANTA CLARA', city: 'CUPERTINO' },
  
  // Riverside County
  '92501': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92502': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92503': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92504': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92505': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92506': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92507': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92508': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92509': { county: 'RIVERSIDE', city: 'RIVERSIDE' },
  '92879': { county: 'RIVERSIDE', city: 'CORONA' },
  '92880': { county: 'RIVERSIDE', city: 'CORONA' },
  '92881': { county: 'RIVERSIDE', city: 'CORONA' },
  '92882': { county: 'RIVERSIDE', city: 'CORONA' },
  '92883': { county: 'RIVERSIDE', city: 'CORONA' },
  '92262': { county: 'RIVERSIDE', city: 'PALM SPRINGS' },
  '92263': { county: 'RIVERSIDE', city: 'PALM SPRINGS' },
  '92264': { county: 'RIVERSIDE', city: 'PALM SPRINGS' },
  '92590': { county: 'RIVERSIDE', city: 'TEMECULA' },
  '92591': { county: 'RIVERSIDE', city: 'TEMECULA' },
  '92592': { county: 'RIVERSIDE', city: 'TEMECULA' },
  '92562': { county: 'RIVERSIDE', city: 'MURRIETA' },
  '92563': { county: 'RIVERSIDE', city: 'MURRIETA' },
  
  // San Bernardino County
  '92401': { county: 'SAN BERNARDINO', city: 'SAN BERNARDINO' },
  '92404': { county: 'SAN BERNARDINO', city: 'SAN BERNARDINO' },
  '92405': { county: 'SAN BERNARDINO', city: 'SAN BERNARDINO' },
  '92407': { county: 'SAN BERNARDINO', city: 'SAN BERNARDINO' },
  '92408': { county: 'SAN BERNARDINO', city: 'SAN BERNARDINO' },
  '92335': { county: 'SAN BERNARDINO', city: 'FONTANA' },
  '92336': { county: 'SAN BERNARDINO', city: 'FONTANA' },
  '92337': { county: 'SAN BERNARDINO', city: 'FONTANA' },
  '91761': { county: 'SAN BERNARDINO', city: 'ONTARIO' },
  '91762': { county: 'SAN BERNARDINO', city: 'ONTARIO' },
  '91764': { county: 'SAN BERNARDINO', city: 'ONTARIO' },
  '91730': { county: 'SAN BERNARDINO', city: 'RANCHO CUCAMONGA' },
  '91737': { county: 'SAN BERNARDINO', city: 'RANCHO CUCAMONGA' },
  '91739': { county: 'SAN BERNARDINO', city: 'RANCHO CUCAMONGA' },
  
  // Sacramento County
  '95814': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95815': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95816': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95817': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95818': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95819': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95820': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95821': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95822': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95823': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95824': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95825': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95826': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95827': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95828': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95829': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95830': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95831': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95832': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95833': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95834': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95835': { county: 'SACRAMENTO', city: 'SACRAMENTO' },
  '95624': { county: 'SACRAMENTO', city: 'ELK GROVE' },
  '95757': { county: 'SACRAMENTO', city: 'ELK GROVE' },
  '95758': { county: 'SACRAMENTO', city: 'ELK GROVE' },
  '95630': { county: 'SACRAMENTO', city: 'FOLSOM' },
  '95661': { county: 'SACRAMENTO', city: 'ROSEVILLE' },
};

// California ZIP code ranges (for validation)
const CA_ZIP_RANGES = [
  { start: 90001, end: 96162 }
];

/**
 * Check if a ZIP code is in California
 * @param {string} zip - ZIP code to check
 * @returns {boolean}
 */
export const isCaliforniaZip = (zip) => {
  const zipNum = parseInt(zip, 10);
  if (isNaN(zipNum)) return false;
  
  return CA_ZIP_RANGES.some(range => zipNum >= range.start && zipNum <= range.end);
};

/**
 * Validate a US ZIP code format
 * @param {string} zip - ZIP code to validate
 * @returns {boolean}
 */
export const isValidZipCode = (zip) => {
  if (!zip) return false;
  const cleanZip = zip.toString().trim().replace(/\D/g, '');
  return /^\d{5}$/.test(cleanZip);
};

/**
 * Get location info from ZIP code
 * @param {string} zip - ZIP code
 * @returns {object|null}
 */
export const getLocationFromZip = (zip) => {
  if (!isValidZipCode(zip)) return null;
  const cleanZip = zip.toString().trim().replace(/\D/g, '');
  return CA_ZIP_MAPPING[cleanZip] || null;
};

/**
 * Get tax rate for a California ZIP code
 * @param {string} zip - ZIP code
 * @returns {number} Tax rate as a decimal (e.g., 0.0875 for 8.75%)
 */
export const getCaliforniaTaxRate = (zip) => {
  const location = getLocationFromZip(zip);
  
  if (location) {
    const countyData = CA_COUNTY_RATES[location.county];
    if (countyData) {
      // Check for city-specific rate first
      const cityRate = countyData.cities[location.city];
      if (cityRate) {
        return cityRate / 100;
      }
      // Fall back to county base rate
      return countyData.baseRate / 100;
    }
  }
  
  // Default California rate if ZIP not in our database
  // Use the state minimum rate
  return 0.0725; // 7.25% minimum CA rate
};

/**
 * Calculate sales tax based on shipping ZIP code
 * @param {number} subtotal - Order subtotal
 * @param {string} zip - Shipping ZIP code
 * @param {boolean} isTaxExempt - Whether customer is tax-exempt
 * @returns {object} Tax calculation result
 */
export const calculateSalesTax = (subtotal, zip, isTaxExempt = false) => {
  // Default response for no tax
  const noTaxResult = {
    taxRate: 0,
    taxAmount: 0,
    taxRateDisplay: '0%',
    taxDescription: 'No tax applicable',
    isCaliforniaNexus: false,
    location: null,
    breakdown: null,
  };
  
  // Check for tax-exempt customer
  if (isTaxExempt) {
    return {
      ...noTaxResult,
      taxDescription: 'Tax-exempt customer',
    };
  }
  
  // Validate ZIP code
  if (!isValidZipCode(zip)) {
    return {
      ...noTaxResult,
      taxDescription: 'Enter ZIP code to calculate tax',
      error: 'Invalid ZIP code format',
    };
  }
  
  const cleanZip = zip.toString().trim().replace(/\D/g, '');
  
  // Check if ZIP is in California
  if (!isCaliforniaZip(cleanZip)) {
    return {
      ...noTaxResult,
      taxDescription: 'No tax for out-of-state orders',
      isOutOfState: true,
    };
  }
  
  // Get California tax rate
  const taxRate = getCaliforniaTaxRate(cleanZip);
  const taxAmount = subtotal * taxRate;
  const location = getLocationFromZip(cleanZip);
  
  // Create tax breakdown
  let breakdown = {
    state: 6.00,
    county: 0.25,
    city: 0,
    district: 0,
  };
  
  if (location) {
    const countyData = CA_COUNTY_RATES[location.county];
    if (countyData) {
      const cityRate = countyData.cities[location.city] || countyData.baseRate;
      const additionalRate = cityRate - 6.25; // State + county base
      breakdown = {
        state: 6.00,
        county: 0.25,
        city: Math.max(0, additionalRate - 1.0), // Estimate city portion
        district: Math.min(1.5, additionalRate), // Estimate district portion
      };
    }
  }
  
  return {
    taxRate,
    taxAmount: Math.round(taxAmount * 100) / 100, // Round to cents
    taxRateDisplay: `${(taxRate * 100).toFixed(2)}%`,
    taxDescription: location 
      ? `Sales Tax (CA – ${location.city}, ${cleanZip})`
      : `Sales Tax (CA – ZIP ${cleanZip})`,
    isCaliforniaNexus: true,
    location,
    breakdown,
    zipCode: cleanZip,
  };
};

/**
 * Format tax rate for display
 * @param {number} rate - Tax rate as decimal
 * @returns {string}
 */
export const formatTaxRate = (rate) => {
  return `${(rate * 100).toFixed(2)}%`;
};

/**
 * Get tax rate source description (for admin view)
 * @param {string} zip - ZIP code
 * @returns {string}
 */
export const getTaxRateSource = (zip) => {
  const location = getLocationFromZip(zip);
  if (!location) {
    return 'Default CA rate (ZIP not in database)';
  }
  
  const countyData = CA_COUNTY_RATES[location.county];
  if (!countyData) {
    return 'Default CA rate (county not in database)';
  }
  
  const cityRate = countyData.cities[location.city];
  if (cityRate) {
    return `City-specific rate: ${location.city}, ${location.county} County`;
  }
  
  return `County rate: ${location.county} County`;
};

// Business constants
export const BUSINESS_NEXUS = {
  city: 'Santa Ana',
  state: 'CA',
  zip: '92705',
  county: 'Orange',
};

export default {
  calculateSalesTax,
  isCaliforniaZip,
  isValidZipCode,
  getLocationFromZip,
  getCaliforniaTaxRate,
  formatTaxRate,
  getTaxRateSource,
  BUSINESS_NEXUS,
};
